/**
 * 安聆 SafeListen - 主应用
 * 监测端/接收端切换、配对码、AI 监听、常亮、多端守护状态
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfigProvider, Select, message } from 'antd';
import { supabase, TABLE_ACTIVE_CODES } from './utils/supabase';
import zhCN from 'antd/locale/zh_CN';
import zhTW from 'antd/locale/zh_TW';
import enUS from 'antd/locale/en_US';
import esES from 'antd/locale/es_ES';
import frFR from 'antd/locale/fr_FR';
import deDE from 'antd/locale/de_DE';
import jaJP from 'antd/locale/ja_JP';
import ptBR from 'antd/locale/pt_BR';
import koKR from 'antd/locale/ko_KR';
import arEG from 'antd/locale/ar_EG';
import itIT from 'antd/locale/it_IT';
import ruRU from 'antd/locale/ru_RU';
import viVN from 'antd/locale/vi_VN';
import thTH from 'antd/locale/th_TH';
import idID from 'antd/locale/id_ID';
import ModeSwitch from './components/ModeSwitch';
import HashGenerator from './components/HashGenerator';
import AudioMonitor from './components/AudioMonitor';
import KeepScreen from './components/KeepScreen';
import GuardStatus from './components/GuardStatus';
import AlertLog from './components/AlertLog';
import './App.css';

const ANTD_LOCALES = {
  zh: zhCN,
  'zh-TW': zhTW,
  en: enUS,
  es: esES,
  fr: frFR,
  de: deDE,
  ja: jaJP,
  pt: ptBR,
  ko: koKR,
  ar: arEG,
  it: itIT,
  ru: ruRU,
  vi: viVN,
  th: thTH,
  id: idID,
};

const LANG_OPTIONS = [
  { key: 'zh', label: '简体中文' },
  { key: 'zh-TW', label: '繁體中文' },
  { key: 'en', label: 'English' },
  { key: 'es', label: 'Español' },
  { key: 'fr', label: 'Français' },
  { key: 'de', label: 'Deutsch' },
  { key: 'ja', label: '日本語' },
  { key: 'pt', label: 'Português' },
  { key: 'ko', label: '한국어' },
  { key: 'ar', label: 'العربية' },
  { key: 'it', label: 'Italiano' },
  { key: 'ru', label: 'Русский' },
  { key: 'vi', label: 'Tiếng Việt' },
  { key: 'th', label: 'ไทย' },
  { key: 'id', label: 'Bahasa Indonesia' },
];

const STORAGE_KEYS = {
  isMonitor: 'safelisten-isMonitor',
  pairingCode: 'safelisten-pairingCode',
  receiverBound: 'safelisten-receiverBound',
  boundPairingCode: 'safelisten-boundPairingCode',
};

function loadState() {
  try {
    const isMonitor = localStorage.getItem(STORAGE_KEYS.isMonitor);
    const pairingCode = localStorage.getItem(STORAGE_KEYS.pairingCode) || '';
    const receiverBound = localStorage.getItem(STORAGE_KEYS.receiverBound);
    const boundPairingCode = localStorage.getItem(STORAGE_KEYS.boundPairingCode) || '';
    return {
      isMonitor: isMonitor !== 'false',
      pairingCode,
      receiverBound: receiverBound === 'true',
      boundPairingCode,
    };
  } catch (_) {
    return { isMonitor: true, pairingCode: '', receiverBound: false, boundPairingCode: '' };
  }
}

export default function App() {
  const { t, i18n } = useTranslation();
  const [state, setState] = useState(loadState);
  const [receiverCount, setReceiverCount] = useState(0);
  const [guardActive, setGuardActive] = useState(false);
  const [alertLog, setAlertLog] = useState([]);
  const { isMonitor, pairingCode, receiverBound, boundPairingCode } = state;
  const MAX_ALERT_LOG = 20;

  const currentLang = i18n.language || 'zh';
  const antdLocale = ANTD_LOCALES[currentLang] || ANTD_LOCALES[currentLang?.split('-')[0]] || zhCN;

  useEffect(() => {
    document.documentElement.lang = i18n.language || 'zh';
    document.documentElement.dir = (currentLang === 'ar' || currentLang?.startsWith('ar')) ? 'rtl' : 'ltr';
  }, [currentLang, i18n.language]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.isMonitor, String(isMonitor));
      localStorage.setItem(STORAGE_KEYS.pairingCode, pairingCode || '');
      localStorage.setItem(STORAGE_KEYS.receiverBound, String(receiverBound));
      localStorage.setItem(STORAGE_KEYS.boundPairingCode, boundPairingCode || '');
    } catch (_) {}
  }, [isMonitor, pairingCode, receiverBound, boundPairingCode]);

  const handleModeChange = useCallback((toMonitor) => {
    setState((prev) => ({
      ...prev,
      isMonitor: !!toMonitor,
      receiverBound: toMonitor ? false : prev.receiverBound,
      boundPairingCode: toMonitor ? '' : prev.boundPairingCode,
    }));
    if (!toMonitor) {
      try {
        localStorage.setItem(STORAGE_KEYS.receiverBound, 'false');
        localStorage.setItem(STORAGE_KEYS.boundPairingCode, '');
      } catch (_) {}
    }
  }, []);

  const handlePairingSubmit = useCallback((code) => {
    if (!code) return;
    setState((prev) => ({ ...prev, receiverBound: true, boundPairingCode: code }));
  }, []);

  const handleReceiverUnbind = useCallback(() => {
    setState((prev) => ({ ...prev, receiverBound: false, boundPairingCode: '' }));
    setAlertLog([]);
    try {
      localStorage.setItem(STORAGE_KEYS.receiverBound, 'false');
      localStorage.setItem(STORAGE_KEYS.boundPairingCode, '');
    } catch (_) {}
  }, []);

  const tRef = useRef(t);
  tRef.current = t;

  useEffect(() => {
    if (!supabase || isMonitor || !receiverBound || !boundPairingCode) return;
    const hashPart = boundPairingCode.split('-')[1]?.trim().toUpperCase();
    if (!hashPart || hashPart.length !== 6) return;
    const channelName = `room:${hashPart}`;
    const ch = supabase.channel(channelName)
      .on('postgres_changes', { event: 'DELETE', schema: 'public', table: TABLE_ACTIVE_CODES, filter: `hash=eq.${hashPart}` }, () => {
        setState((prev) => ({ ...prev, receiverBound: false, boundPairingCode: '' }));
        try {
          localStorage.setItem(STORAGE_KEYS.receiverBound, 'false');
          localStorage.setItem(STORAGE_KEYS.boundPairingCode, '');
        } catch (e) {}
        message.info(tRef.current('guard.disconnected'));
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: TABLE_ACTIVE_CODES, filter: `hash=eq.${hashPart}` }, () => {
        message.warning(tRef.current('audio.alertPush') || '监测端触发预警');
        setAlertLog((prev) => [...prev.slice(-(MAX_ALERT_LOG - 1)), { id: Date.now(), at: new Date().toISOString() }]);
      })
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') ch.track({ role: 'receiver' });
      });
    return () => { ch.unsubscribe(); };
  }, [isMonitor, receiverBound, boundPairingCode]);

  useEffect(() => {
    if (!supabase || !isMonitor || !pairingCode) {
      if (!isMonitor || !pairingCode) setReceiverCount(0);
      return;
    }
    const hashPart = pairingCode.split('-')[1]?.trim().toUpperCase();
    if (!hashPart || hashPart.length !== 6) {
      setReceiverCount(0);
      return;
    }
    const channelName = `room:${hashPart}`;
    const ch = supabase.channel(channelName)
      .on('presence', { event: 'sync' }, () => {
        const state = ch.presenceState();
        const receivers = Object.values(state).flat().filter((p) => p.role === 'receiver');
        setReceiverCount(receivers.length);
      })
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') ch.track({ role: 'monitor' });
      });
    return () => { ch.unsubscribe(); };
  }, [isMonitor, pairingCode]);

  const setPairingCode = useCallback((code) => {
    setState((prev) => ({ ...prev, pairingCode: code || '' }));
  }, []);

  const handleMonitorAlert = useCallback(() => {
    if (!supabase || !pairingCode) return;
    const h = pairingCode.split('-')[1]?.trim().toUpperCase();
    if (h && h.length === 6) supabase.from(TABLE_ACTIVE_CODES).update({ last_alert: new Date().toISOString() }).eq('hash', h).then(() => {});
  }, [pairingCode]);

  return (
    <ConfigProvider locale={antdLocale} theme={{
      token: {
        colorPrimary: '#059669',
        colorSuccess: '#059669',
        colorError: '#dc2626',
        colorWarning: '#dc2626',
        borderRadius: 8,
      },
    }}>
      <div className="app">
        <header className="app-header">
          <img src="/logo.png" alt="安聆 SafeListen" className="brand-logo" />
          <p className="brand-subtitle">{t('subtitle')}</p>
          <Select
            className="lang-select"
            value={currentLang}
            onChange={(v) => i18n.changeLanguage(v)}
            options={LANG_OPTIONS.map(({ key, label }) => ({ value: key, label }))}
            size="small"
          />
        </header>

        <GuardStatus
          isMonitor={isMonitor}
          receiverCount={receiverCount}
          receiverBound={receiverBound}
          guardActive={guardActive}
        />

        <ModeSwitch
          isMonitor={isMonitor}
          onModeChange={handleModeChange}
          pairingCode={pairingCode}
          boundPairingCode={boundPairingCode}
          onPairingSubmit={handlePairingSubmit}
          onReceiverUnbind={handleReceiverUnbind}
          receiverBound={receiverBound}
        />

        {!isMonitor && receiverBound && <AlertLog items={alertLog} />}

        <HashGenerator
          isMonitor={isMonitor}
          currentCode={pairingCode}
          onPairingCodeChange={setPairingCode}
        />

        <AudioMonitor
          isMonitor={isMonitor}
          onMonitoringChange={setGuardActive}
          onAlert={supabase && pairingCode ? handleMonitorAlert : undefined}
        />

        <KeepScreen isMonitor={isMonitor} />

        <footer className="app-footer">
          <span className="safelisten-text-alt">安聆 SafeListen · Baby Care · Global Guard</span>
        </footer>
      </div>
    </ConfigProvider>
  );
}
